import { forwardRef, ReactNode } from "react";

import {
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
  DialogActions as MuiDialogActions,
  Box,
  ThemeProvider,
  DialogProps,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { StorybookDialogPreview } from "./StorybookDialogPreview";

import { FormButton } from "@/forms-design-system/FormButton";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { useDynamicFormV2Theme } from "@/forms-design-system/Tokens/dynamicFormV2Theme";
import { Cross } from "@/icons/outlined";

type FormDialogLayoutProps = {
  noPadding?: boolean;
  slots: {
    closeButton: ReactNode;
    title: ReactNode;
    content: ReactNode;
    actions?: ReactNode[];
  };
  slotProps?: {
    content?: {
      id?: string;
    };
  };
};

export const FormDialogLayout = ({ noPadding = false, slots, slotProps }: FormDialogLayoutProps) => {
  const theme = useTheme();

  return (
    <>
      <MuiDialogTitle
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          padding: theme.spacing(2, 3),
          borderBlockEnd: `1px solid ${theme.palette.formsComponents.divider.borderColor}`,
        }}
      >
        <FormTypography variant="h6" color={theme.palette.formsComponents.dialog.titleColor} sx={{ paddingBlock: 0.5 }}>
          {slots.title}
        </FormTypography>
        {slots.closeButton}
      </MuiDialogTitle>
      <Box
        id={slotProps?.content?.id}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: noPadding ? 0 : theme.spacing(2, 3),
          ...theme.typography.body2,
          color: theme.palette.formsComponents.dialog.textColor,
          overflowY: "auto",
          scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`,
          ["&::-webkit-scrollbar-track"]: {
            background: "transparent",
          },
          ["&::-webkit-scrollbar-thumb"]: {
            background: theme.palette.formsComponents.global.scrollbarThumbColor,
          },
        }}
      >
        {slots.content}
      </Box>
      {slots.actions && (
        <MuiDialogActions
          disableSpacing
          sx={{
            flexDirection: { xs: "column", md: "row-reverse" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: { xs: "stretch", md: "flex-start" },
            gap: 1,
            padding: theme.spacing(2, 3),
            borderBlockStart: `1px solid ${theme.palette.formsComponents.divider.borderColor}`,
          }}
        >
          {slots.actions}
        </MuiDialogActions>
      )}
    </>
  );
};

export type FormDialogProps = Pick<FormDialogLayoutProps, "slotProps"> & {
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode[];
  open?: boolean;
  onClose?: () => void;
  inline?: boolean;
  noPadding?: boolean;
  maxWidth?: DialogProps["maxWidth"];
  hideCloseButton?: boolean;
};

export const InnerFormDialog = forwardRef<HTMLDivElement, FormDialogProps>(
  (
    {
      inline,
      open = false,
      title,
      children,
      actions,
      noPadding,
      slotProps,
      maxWidth = "xs",
      hideCloseButton = false,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.dialog) {
      return <InvalidThemeErrorMessage />;
    }

    const content = (
      <FormDialogLayout
        noPadding={noPadding}
        slots={{
          closeButton: hideCloseButton ? null : (
            <FormButton
              variant="ghost"
              startIcon={<Cross />}
              onClick={rest.onClose}
              iconButton
              sx={{ flexShrink: 0 }}
            />
          ),
          title,
          content: children,
          actions,
        }}
        slotProps={slotProps}
      />
    );

    if (inline) {
      return (
        <StorybookDialogPreview ref={ref} {...rest}>
          {content}
        </StorybookDialogPreview>
      );
    }

    return (
      <MuiDialog
        ref={ref}
        open={open}
        {...rest}
        maxWidth={maxWidth}
        sx={{ ["& .MuiPaper-root"]: { width: "100%", overflowY: "visible" } }}
      >
        {content}
      </MuiDialog>
    );
  }
);

InnerFormDialog.displayName = "InnerFormDialog";

export const FormDialog = forwardRef<HTMLDivElement, FormDialogProps>((props, ref) => {
  const theme = useTheme();

  if (!theme.palette.forms || !theme.palette.metadata) {
    return <InvalidThemeErrorMessage />;
  }

  // This hook is rendered conditionally, but that should never happen unless in we're in dev mode
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dialogTheme = useDynamicFormV2Theme(theme.palette.metadata.baseColor, "light");

  return (
    <ThemeProvider theme={dialogTheme}>
      <InnerFormDialog ref={ref} {...props} />
    </ThemeProvider>
  );
});

FormDialog.displayName = "FormDialog";
